// Build ID is stamped into index.html and version.json by `buildVersion()` in @suntex.dev/vite.
const META_NAME = 'suntex-build-id';
const VERSION_URL = '/version.json';
// eslint-disable-next-line no-magic-numbers -- 5 minute poll interval
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const PRELOAD_RELOAD_KEY = 'suntex:preload-reload-at';
const PRELOAD_RELOAD_GUARD_MS = 10_000;

let started = false;
let stale = false;
let intervalId: ReturnType<typeof setInterval> | undefined;
let onVisible: (() => void) | undefined;

function stop(): void {
  if (intervalId !== undefined) clearInterval(intervalId);
  if (onVisible) document.removeEventListener('visibilitychange', onVisible);
}

function onPreloadError(event: Event): void {
  const last = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? 0);
  if (Date.now() - last > PRELOAD_RELOAD_GUARD_MS) {
    event.preventDefault();
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }
  // otherwise let the error propagate so the route errorElement shows
}

function readBuildId(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null || !('buildId' in data)) {
    return undefined;
  }
  const { buildId } = data as Record<string, unknown>;
  return typeof buildId === 'string' ? buildId : undefined;
}

export function startBuildVersionWatch(): void {
  if (started) return;
  started = true;

  window.addEventListener('vite:preloadError', onPreloadError);

  const current = document.querySelector<HTMLMetaElement>(
    `meta[name="${META_NAME}"]`,
  )?.content;
  if (!current) return; // dev server/tests — no build id to compare against

  async function check(): Promise<void> {
    if (stale) return;
    try {
      const res = await fetch(VERSION_URL, { cache: 'no-store' });
      if (!res.ok) return;
      const data: unknown = await res.json();
      const buildId = readBuildId(data);
      if (buildId && buildId !== current) {
        stale = true;
        stop();
      }
    } catch {
      // network/parse errors are ignored — retry on next poll
    }
  }

  intervalId = setInterval(() => void check(), POLL_INTERVAL_MS);
  onVisible = () => {
    if (document.visibilityState === 'visible') void check();
  };
  document.addEventListener('visibilitychange', onVisible);
}

export function isBuildStale(): boolean {
  return stale;
}
